import { useState, useMemo, useEffect } from 'react';
import { formatUtcDateTimeToTimeZone } from '@belong/common';
import { Text, Spinner } from '@belong/ui';
import { useInspectionAvailabilityDates } from 'accounts/hooks/hooks';
import clsx from 'clsx';
import { format, parseISO } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { IconLeftCaret, IconRightCaret, IconClose } from '@belong/icons';

interface MultiselectDateTimeRangePickerProps {
  unitId: string;
  timeZone?: string;
  title?: string;
  maxSelections?: number;
  minSelections?: number;
  onChange?: (
    selections: DateTimeSelection[],
    /**
     * The date the picker is focused on, or `null` when the current one is
     * toggled off. Omitted entirely by the "remove all selections for this date"
     * button below — see the `hasSelectedDate` note in
     * docs/development/typecheck-baseline-followups.md.
     */
    selectedDate?: string | null,
  ) => void;
  initialSelections?: DateTimeSelection[];
  onChangeCustom?: (selections: DateTimeSelection[]) => void;
  isValid?: boolean;
  validationError?: string;
  showDateValidationErrors?: boolean;
  showTimeValidationErrors?: boolean;
  onDateValidationReset?: () => void;
  onTimeValidationReset?: () => void;
}

export interface DateTimeSelection {
  date: string; // ISO date string
  range: 'morning' | 'afternoon' | 'evening';
  displayText: string;
  availableTimes: string[]; // All available times in this range for this date
}

interface TimeSlot {
  key: string;
  originalDateTime?: string;
}

const TIME_RANGES = {
  morning: {
    label: 'Morning',
    timeRange: '(9AM - Noon)',
    startHour: 9,
    endHour: 12,
  },
  afternoon: {
    label: 'Afternoon',
    timeRange: '(Noon - 3PM)',
    startHour: 12,
    endHour: 15,
  },
  evening: {
    label: 'Evening',
    timeRange: '(3PM - 6PM)',
    startHour: 15,
    endHour: 18,
  },
};

const DATES_PER_PAGE = 4;
const MOBILE_DATES_PER_PAGE = 2;

function mergeTimeRanges(dateSelections: DateTimeSelection[]): string {
  if (dateSelections.length === 0) return '';
  if (dateSelections.length === 1) {
    const range = TIME_RANGES[dateSelections[0]!.range];
    return formatTimeRange(range.startHour, range.endHour);
  }

  const sortedSelections = dateSelections.sort((a, b) => {
    const aStart = TIME_RANGES[a.range].startHour;
    const bStart = TIME_RANGES[b.range].startHour;
    return aStart - bStart;
  });

  const mergedRanges: string[] = [];
  // Both index reads below are inside the length checks that guarantee them:
  // this point is only reached with two or more selections, and the loop runs
  // while `i` is in range.
  let currentStart = TIME_RANGES[sortedSelections[0]!.range].startHour;
  let currentEnd = TIME_RANGES[sortedSelections[0]!.range].endHour;

  for (let i = 1; i < sortedSelections.length; i++) {
    const nextStart = TIME_RANGES[sortedSelections[i]!.range].startHour;
    const nextEnd = TIME_RANGES[sortedSelections[i]!.range].endHour;

    // If ranges are consecutive (no gap), extend current range
    if (nextStart === currentEnd) {
      currentEnd = nextEnd;
    } else {
      // Gap found, save current range and start new one
      mergedRanges.push(formatTimeRange(currentStart, currentEnd));
      currentStart = nextStart;
      currentEnd = nextEnd;
    }
  }

  mergedRanges.push(formatTimeRange(currentStart, currentEnd));

  return mergedRanges.join(' | ');
}

// Helper function to format time range
function formatTimeRange(startHour: number, endHour: number): string {
  const formatHour = (hour: number) => {
    if (hour === 0) return '12AM';
    if (hour < 12) return `${hour}AM`;
    if (hour === 12) return 'Noon';
    return `${hour - 12}PM`;
  };

  return `${formatHour(startHour)} - ${formatHour(endHour)}`;
}

function convertToUTC(dateTimeString: string): string {
  try {
    const date = parseISO(dateTimeString);

    return date.toISOString();
  } catch (error) {
    return dateTimeString;
  }
}

export function MultiselectDateTimeRangePicker({
  unitId,
  timeZone = 'America/Los_Angeles',
  title = 'Select 3 Times',
  maxSelections = 10,
  minSelections = 3,
  onChange,
  initialSelections = [],
  onChangeCustom,
  showDateValidationErrors = false,
  showTimeValidationErrors = false,
  onDateValidationReset,
  onTimeValidationReset,
}: MultiselectDateTimeRangePickerProps) {
  const [currentPage, setCurrentPage] = useState(0);
  const [selections, setSelections] =
    useState<DateTimeSelection[]>(initialSelections);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(false);

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Use a broad date range to let the API determine actual availability
  const dateRange = useMemo(() => {
    const now = new Date();
    // Use a broad range - API will return only available dates
    const startDate = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
    );
    const endDate = new Date(now.getTime() + 60 * 24 * 60 * 60 * 1000); // 60 days ahead

    // Freeze the objects to ensure they maintain reference equality
    Object.freeze(startDate);
    Object.freeze(endDate);

    return { startDate, endDate };
  }, []);

  // Use the existing hook to get availability data - API determines actual available dates
  const { availabilityDates, isLoadingDates } = useInspectionAvailabilityDates({
    unit: { basicInfo: { id: unitId } },
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
    timeZone,
  });
  // Process availability data to group by date and determine available ranges
  const processedDates = useMemo(() => {
    if (!availabilityDates) return [];

    const datesWithRanges = Object.entries(availabilityDates)
      .map(([dateStr, times]) => {
        // Parse the date string directly as local date
        const zonedDate = new Date(dateStr + 'T00:00:00');

        const availableRanges: ('morning' | 'afternoon' | 'evening')[] = [];

        // Check each time range for availability
        Object.entries(TIME_RANGES).forEach(([rangeKey, rangeInfo]) => {
          const rangeTimes = (times as TimeSlot[]).filter(
            (timeObj: TimeSlot) => {
              // `split` always yields at least one element.
              const hour = parseInt(timeObj.key.split(':')[0]!, 10);
              return hour >= rangeInfo.startHour && hour < rangeInfo.endHour;
            },
          );

          if (rangeTimes.length > 0) {
            availableRanges.push(
              rangeKey as 'morning' | 'afternoon' | 'evening',
            );
          }
        });

        // Only include dates that have at least one available range
        if (availableRanges.length > 0) {
          return {
            date: dateStr,
            zonedDate,
            availableRanges,
            allTimes: times,
          };
        }

        return null;
      })
      // `filter(Boolean)` drops the nulls at runtime but not from the element
      // type, which is why every `dateInfo`/`d` below read as possibly-null.
      .filter((entry): entry is NonNullable<typeof entry> => entry !== null);

    return datesWithRanges;
  }, [availabilityDates, timeZone]);

  // Group dates into pages - different page sizes for mobile vs desktop
  const pages = useMemo(() => {
    const result = [];
    const datesPerPage = isMobile ? MOBILE_DATES_PER_PAGE : DATES_PER_PAGE;
    for (let i = 0; i < processedDates.length; i += datesPerPage) {
      const page = processedDates.slice(i, i + datesPerPage);

      result.push(page);
    }
    return result;
  }, [processedDates, isMobile]);

  // Get available times for a specific date and range
  const getAvailableTimesForRange = (
    dateStr: string,
    range: 'morning' | 'afternoon' | 'evening',
  ) => {
    if (!availabilityDates || !availabilityDates[dateStr]) return [];

    const rangeInfo = TIME_RANGES[range];

    const filteredTimes = availabilityDates[dateStr].filter(
      (timeObj: TimeSlot) => {
        // As above: `split` always yields at least one element.
        const hour = parseInt(timeObj.key.split(':')[0]!, 10);
        const isInRange =
          hour >= rangeInfo.startHour && hour < rangeInfo.endHour;

        return isInRange;
      },
    );

    const result = filteredTimes.map((timeObj: TimeSlot) => {
      if (timeObj.originalDateTime) {
        const converted = convertToUTC(timeObj.originalDateTime);

        return converted;
      } else {
        return `${dateStr}T${timeObj.key}:00`;
      }
    });

    return result;
  };

  // Handle date selection
  const handleDateSelect = (dateStr: string) => {
    if (
      selections.length >= maxSelections &&
      !selections.some((s) => s.date === dateStr)
    )
      return;

    // Reset both date and time validation states when user interacts with dates
    if (showDateValidationErrors && onDateValidationReset) {
      onDateValidationReset();
    }
    // Reset time validation state when user selects a time range
    if (showTimeValidationErrors && onTimeValidationReset) {
      onTimeValidationReset();
    }

    const newSelectedDate = selectedDate === dateStr ? null : dateStr;

    setSelectedDate(newSelectedDate);

    onChange?.(selections, newSelectedDate);
  };

  // Handle time range selection
  const handleTimeRangeSelect = (
    dateStr: string,
    range: 'morning' | 'afternoon' | 'evening',
  ) => {
    const availableTimes = getAvailableTimesForRange(dateStr, range);

    if (availableTimes.length === 0) return;

    // Reset time validation state when user selects a time range
    if (showTimeValidationErrors && onTimeValidationReset) {
      onTimeValidationReset();
    }

    const zonedDate = utcToZonedTime(parseISO(dateStr), timeZone);
    const dateLabel = getDateLabel(zonedDate);

    const newSelection: DateTimeSelection = {
      date: dateStr,
      range,
      displayText: `${dateLabel} ${TIME_RANGES[range].timeRange}`,
      availableTimes,
    };

    const updatedSelections = [...selections];
    const existingIndex = updatedSelections.findIndex(
      (s) => s.date === dateStr && s.range === range,
    );

    if (existingIndex >= 0) {
      // Remove existing selection
      updatedSelections.splice(existingIndex, 1);
    } else {
      // Add new selection (if under limit)
      if (updatedSelections.length < maxSelections) {
        updatedSelections.push(newSelection);
      }
    }

    setSelections(updatedSelections);
    onChange?.(updatedSelections, selectedDate);
    onChangeCustom?.(updatedSelections);
  };

  // Get date label for display - use explicit dates instead of relative terms
  const getDateLabel = (date: Date) => {
    return format(date, 'EEEE');
  };

  // Check if arrows should be disabled
  const isLeftDisabled = currentPage === 0;
  const isRightDisabled = currentPage === pages.length - 1;
  const areArrowsDisabled =
    selectedDate !== null && !selections.some((s) => s.date === selectedDate);

  if (isLoadingDates) {
    return (
      <div className="mt-2xl min-h-[200px]">
        <Spinner />
      </div>
    );
  }

  if (pages.length === 0) {
    return (
      <div className="mt-2xl text-center">
        <Text>No available dates found.</Text>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Title and Counter */}
      <div className="mb-lg flex flex-col items-center justify-between">
        <Text variant="h3" fontWeight="semibold" className="text-navy">
          {title}
        </Text>
        <div className="mt-2xs flex space-x-xs md:mt-sm">
          {Array.from({ length: minSelections }, (_, i) => (
            <div
              key={i}
              style={{ width: 18, height: 4 }}
              className={clsx(
                'rounded-full',
                i < selections.length ? 'bg-green' : 'bg-gray',
              )}
            />
          ))}
        </div>
      </div>

      {/* Carousel */}
      <div className="relative">
        {/* Left Arrow */}
        <button
          className={clsx(
            'absolute -top-2xl left-0 z-10 flex h-sm w-sm items-center justify-center md:top-1/2 md:mt-0 md:-translate-y-1/2',
            isLeftDisabled || areArrowsDisabled
              ? 'cursor-not-allowed text-gray'
              : 'hover:text-dark-navy',
          )}
          onClick={() =>
            !isLeftDisabled &&
            !areArrowsDisabled &&
            setCurrentPage(currentPage - 1)
          }
          disabled={isLeftDisabled || areArrowsDisabled}
          type="button"
        >
          <IconLeftCaret className="h-sm w-sm" />
        </button>

        {/* Two-Row Layout Container */}
        <div className="overflow-hidden px-2xs md:px-lg">
          {/* First Row: Date Cards */}
          <div className="mb-md grid grid-cols-2 gap-sm md:grid-cols-4 md:gap-md">
            {pages[currentPage]?.map((dateInfo) => {
              const hasSelections = selections.some(
                (s) => s.date === dateInfo.date,
              );
              const selectionsCount = selections.filter(
                (s) => s.date === dateInfo.date,
              ).length;
              // Show validation error for date cards when overall minimum selections not met
              const showDateValidationError =
                showDateValidationErrors && selections.length < minSelections;

              return (
                <DateCard
                  key={dateInfo.date}
                  dateInfo={dateInfo}
                  isSelected={selectedDate === dateInfo.date}
                  hasSelections={hasSelections}
                  selectionsCount={selectionsCount}
                  onDateSelect={handleDateSelect}
                  isDisabled={selections.length >= maxSelections}
                  showValidationError={showDateValidationError}
                />
              );
            })}
          </div>

          {/* Second Row: Time Range Cards */}
          <div className="grid grid-cols-1 gap-y-2xs md:grid-cols-3 md:gap-md ">
            {Object.entries(TIME_RANGES).map(([rangeKey, rangeInfo]) => {
              // Check if selected date is on current page
              const isSelectedDateOnCurrentPage =
                selectedDate &&
                pages[currentPage]?.some((d) => d.date === selectedDate);

              // Show as available if selected date is on current page AND either not at max selections OR this specific range is already selected
              const isAtMaxSelections = selections.length >= maxSelections;
              const isThisRangeSelected = isSelectedDateOnCurrentPage
                ? selections.some(
                    (s) => s.date === selectedDate && s.range === rangeKey,
                  )
                : false;

              const isAvailable =
                isSelectedDateOnCurrentPage &&
                (!isAtMaxSelections || isThisRangeSelected)
                  ? pages[currentPage]
                      ?.find((d) => d.date === selectedDate)
                      ?.availableRanges.includes(
                        rangeKey as 'morning' | 'afternoon' | 'evening',
                      )
                  : false;

              const isSelected = isSelectedDateOnCurrentPage
                ? selections.some(
                    (s) => s.date === selectedDate && s.range === rangeKey,
                  )
                : false;

              // Check if validation failed - date is selected but no time ranges are selected for this date
              const hasTimeSelectionForSelectedDate =
                isSelectedDateOnCurrentPage
                  ? selections.some((s) => s.date === selectedDate)
                  : false;

              const showValidationError =
                showTimeValidationErrors &&
                isSelectedDateOnCurrentPage &&
                !hasTimeSelectionForSelectedDate;

              return (
                <button
                  key={rangeKey}
                  style={{ height: isMobile ? 70 : 110 }}
                  className={clsx(
                    'mb-sm w-full rounded border p-sm text-center transition-colors md:p-sm',
                    isSelected
                      ? 'border-navy bg-navy text-white duration-hover hover:border-3 hover:border-solid hover:border-dark-navy'
                      : showValidationError
                        ? 'border-3 border-solid border-red'
                        : isAvailable
                          ? 'bg-white text-navy shadow duration-hover hover:border-3 hover:border-solid hover:border-navy'
                          : 'cursor-not-allowed border-solid border-gray bg-light-gray text-dark-gray',
                  )}
                  onClick={() => {
                    if (isAvailable && selectedDate) {
                      handleTimeRangeSelect(
                        selectedDate,
                        rangeKey as 'morning' | 'afternoon' | 'evening',
                      );
                    }
                  }}
                  disabled={!isAvailable}
                  type="button"
                >
                  <Text fontWeight="semibold" className="mb-0 leading-p2">
                    {rangeInfo.label}
                  </Text>
                  <Text className="text-p1">{rangeInfo.timeRange}</Text>
                </button>
              );
            })}
          </div>
        </div>

        {/* Right Arrow */}
        <button
          className={clsx(
            'absolute -top-2xl right-0 z-10 flex h-sm w-sm items-center justify-center md:top-1/2 md:mt-0 md:-translate-y-1/2',
            isRightDisabled || areArrowsDisabled
              ? 'cursor-not-allowed text-gray'
              : 'hover:text-dark-navy',
          )}
          onClick={() =>
            !isRightDisabled &&
            !areArrowsDisabled &&
            setCurrentPage(currentPage + 1)
          }
          disabled={isRightDisabled || areArrowsDisabled}
          type="button"
        >
          <IconRightCaret className="h-sm w-sm" />
        </button>
      </div>

      {/* Timezone Info */}
      <Text className="text-sm mt-2xs text-right text-dark-gray">
        {`All timezones in ${formatUtcDateTimeToTimeZone({
          dateTime: new Date(),
          format: 'z',
          timeZone,
        })}.`}
      </Text>

      {selections.length > 0 && (
        <div className="mt-xl">
          <div className="space-y-sm">
            {Object.entries(
              selections.reduce(
                (acc, selection) => {
                  if (!acc[selection.date]) {
                    acc[selection.date] = [];
                  }
                  acc[selection.date]!.push(selection);
                  return acc;
                },
                {} as Record<string, DateTimeSelection[]>,
              ),
            ).map(([date, dateSelections]) => {
              const zonedDate = utcToZonedTime(parseISO(date), timeZone);
              const dateLabel = getDateLabel(zonedDate);
              const dateSubLabel = format(zonedDate, 'MMM do, yyyy');
              const timeRanges = mergeTimeRanges(dateSelections);

              return (
                <div
                  key={date}
                  className="flex items-start justify-between px-2xs"
                >
                  <div className="flex flex-col md:flex-row">
                    <Text fontWeight="semibold" className="text-body">
                      {`${dateLabel}, ${dateSubLabel}`}
                    </Text>
                    <Text fontWeight="semibold" className="text-body md:ml-xs">
                      ({timeRanges})
                    </Text>
                  </div>
                  <button
                    className="mt-xs text-gray hover:text-dark-gray"
                    onClick={() => {
                      // Remove all selections for this date
                      const updatedSelections = selections.filter(
                        (s) => s.date !== date,
                      );
                      setSelections(updatedSelections);
                      onChange?.(updatedSelections);
                    }}
                    type="button"
                  >
                    <IconClose
                      width={16}
                      className="text-navy hover:text-dark-navy"
                    />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// Date Card Component
interface DateCardProps {
  dateInfo: {
    date: string;
    zonedDate: Date;
    availableRanges: ('morning' | 'afternoon' | 'evening')[];
    allTimes: TimeSlot[];
  };
  isSelected: boolean;
  hasSelections: boolean;
  selectionsCount: number; // Number of time selections for this date
  onDateSelect: (dateStr: string) => void;
  isDisabled?: boolean;
  showValidationError?: boolean;
}

function DateCard({
  dateInfo,
  isSelected,
  hasSelections,
  selectionsCount,
  onDateSelect,
  isDisabled = false,
  showValidationError = false,
}: DateCardProps) {
  const { date, zonedDate } = dateInfo;
  const dateLabel = format(zonedDate, 'EEEE');
  const dateSubLabel = format(zonedDate, 'MMM do, yyyy');

  return (
    <div
      className={clsx(
        'mt-sm flex min-w-0 flex-1 flex-col justify-start rounded border p-sm shadow transition-colors duration-hover hover:border-3 hover:border-solid',
        {
          'cursor-not-allowed opacity-50': isDisabled,
          'border-red border-solid border-3':
            showValidationError && !isSelected,
          'cursor-pointer': !isDisabled,
          'hover:border-navy': !isSelected,
          'border-navy bg-navy text-white hover:border-dark-navy': isSelected,
          'text-navy border-white':
            !isSelected && !hasSelections && !showValidationError,
          'border-white bg-white text-navy':
            !isSelected && hasSelections && !showValidationError,
        },
      )}
      style={{ height: 110 }}
      onClick={() => !isDisabled && onDateSelect(date)}
    >
      <div className="flex h-full flex-col justify-center text-center">
        <div className="md:mb-xs">
          <Text
            variant="body"
            fontWeight="semibold"
            className={clsx('text-sm', isSelected ? 'text-white' : 'text-navy')}
          >
            {dateLabel}
          </Text>
          <Text
            variant="p2"
            className={clsx(isSelected ? 'text-white' : 'text-navy')}
          >
            {dateSubLabel}
          </Text>
        </div>

        <div
          className="mt-xs flex justify-center space-x-2xs md:mt-0"
          style={{ height: 6 }}
        >
          {selectionsCount > 0 ? (
            Array.from({ length: selectionsCount }, (_, i) => (
              <div
                key={i}
                style={{ width: 6, height: 6 }}
                className="rounded-full bg-green"
              />
            ))
          ) : (
            // Invisible placeholder to maintain consistent spacing in the time cards when the green dots are not visible.
            <div style={{ width: 6, height: 6 }} />
          )}
        </div>
      </div>
    </div>
  );
}
